import type { EChartsOption } from 'echarts';

/** When each line series first reached the chart, by series id. */
export type LineAppearances = ReadonlyMap<string, number>;

export function getLineSeriesIds(option: EChartsOption): string[] {
  if (!Array.isArray(option.series)) {
    return [];
  }

  return option.series.flatMap((series) =>
    series.type === 'line' && series.id !== undefined ? [String(series.id)] : []
  );
}

/** Keeps the time of lines already on the chart, starts new ones at `now` and forgets removed ones. */
export function updateLineAppearances(previous: LineAppearances, lineIds: string[], now: number): LineAppearances {
  return new Map(lineIds.map((id) => [id, previous.get(id) ?? now]));
}

/** Lines whose entry animation has run its course; switching animation off any earlier would cut it short. */
export function getSettledLineIds(appearances: LineAppearances, now: number, entryDurationMs: number): Set<string> {
  return new Set([...appearances].filter(([, appearedAt]) => now - appearedAt >= entryDurationMs).map(([id]) => id));
}

/**
 * Settled lines update with animation off. Updates never move visibly anyway (the update duration is
 * zero), but a line allowed to animate is diffed point by point on every update and every zoom and
 * pan step. A new line keeps animation on, so it still enters the way it always did.
 */
export function withoutLineUpdateAnimation(option: EChartsOption, settledLineIds: ReadonlySet<string>): EChartsOption {
  if (!Array.isArray(option.series)) {
    return option;
  }

  return {
    ...option,
    series: option.series.map((series) =>
      series.type === 'line' && settledLineIds.has(String(series.id)) ? { ...series, animation: false } : series
    ),
  };
}
